import { PERIOD_MONTHS_MAP } from './constants';
import {
  type ChartDataItem,
  type CrossInvoiceItem,
  type CrossInvoicesData,
  type MonthMetadata,
  type PeriodOption,
  type Totals,
} from './types';

export function processChartData(
  data: CrossInvoicesData,
  selectedBusiness?: CrossInvoiceItem | null,
): ChartDataItem[] {
  const monthsMetadata = data.monthsMetadata ?? [];

  return monthsMetadata.map((monthMeta: MonthMetadata) => {
    let monthData = data.dataPayers?.[monthMeta.key] ?? [];
    if (selectedBusiness?.identifier) {
      monthData = monthData.filter(
        (item: CrossInvoiceItem) =>
          item.identifier === selectedBusiness.identifier,
      );
    }

    const totalCrossAmount = monthData.reduce(
      (sum: number, item: CrossInvoiceItem) => sum + (item.crossAmount ?? 0),
      0,
    );
    const totalEmittedAmount = monthData.reduce(
      (sum: number, item: CrossInvoiceItem) => sum + (item.emittedAmount ?? 0),
      0,
    );
    const totalReceivedAmount = monthData.reduce(
      (sum: number, item: CrossInvoiceItem) => sum + (item.recievedAmount ?? 0),
      0,
    );

    return {
      name: monthMeta.displayName,
      'Monto cruzado total': totalCrossAmount,
      'Monto cruzado V': totalEmittedAmount,
      'Monto cruzado C': totalReceivedAmount,
    };
  });
}

export function calculateTotals(chartData: ChartDataItem[]): Totals {
  const totalCrossAmount = chartData.reduce(
    (sum, item) => sum + item['Monto cruzado total'],
    0,
  );
  const totalEmittedAmount = chartData.reduce(
    (sum, item) => sum + item['Monto cruzado V'],
    0,
  );
  const totalReceivedAmount = chartData.reduce(
    (sum, item) => sum + item['Monto cruzado C'],
    0,
  );
  const monthsCount = chartData.length || 1;

  return {
    totalCrossAmount,
    totalEmittedAmount,
    totalReceivedAmount,
    averageCrossAmount: totalCrossAmount / monthsCount,
    averageEmittedAmount: totalEmittedAmount / monthsCount,
    averageReceivedAmount: totalReceivedAmount / monthsCount,
  };
}

export function getFilteredChartData(
  data: ChartDataItem[],
  periodKey: string,
): ChartDataItem[] {
  if (!data.length) return data;
  const months = PERIOD_MONTHS_MAP[periodKey as PeriodOption] ?? 24;
  return data.slice(-months);
}

export function getFilteredTableData(
  crossInvoicesData: CrossInvoicesData,
  selectedMonthsTable: string[],
  selectedBusinessFilter: CrossInvoiceItem | null,
): CrossInvoiceItem[] {
  let dataToFilter: CrossInvoiceItem[];

  if (selectedMonthsTable.length === 0) {
    const total = crossInvoicesData.dataPayers?.total;
    dataToFilter = Array.isArray(total) ? total : [];
  } else {
    const combinedData = selectedMonthsTable.reduce<CrossInvoiceItem[]>(
      (acc, monthKey) => {
        const monthData = crossInvoicesData.dataPayers[monthKey];
        if (Array.isArray(monthData)) return acc.concat(monthData);
        return acc;
      },
      [],
    );

    const groupedData = combinedData.reduce<Record<string, CrossInvoiceItem>>(
      (acc, item) => {
        if (!acc[item.identifier]) {
          acc[item.identifier] = {
            identifier: item.identifier,
            name: item.name,
            emittedAmount: 0,
            emittedCount: 0,
            recievedAmount: 0,
            recievedCount: 0,
            crossInvoices: 0,
            crossAmount: 0,
          };
        }
        const row = acc[item.identifier];
        row.emittedAmount += item.emittedAmount ?? 0;
        row.emittedCount += item.emittedCount ?? 0;
        row.recievedAmount += item.recievedAmount ?? 0;
        row.recievedCount += item.recievedCount ?? 0;
        row.crossInvoices += item.crossInvoices ?? 0;
        row.crossAmount += item.crossAmount ?? 0;
        return acc;
      },
      {},
    );

    dataToFilter = Object.values(groupedData).map((row) => {
      const percent =
        row.emittedAmount > 0
          ? Math.round((row.recievedAmount / row.emittedAmount) * 10000) / 100
          : null;
      return { ...row, receivedOverEmittedPercent: percent };
    });
  }

  if (selectedBusinessFilter) {
    return dataToFilter.filter(
      (item) => item.identifier === selectedBusinessFilter.identifier,
    );
  }
  return dataToFilter;
}
